import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const PERIODS = ["AM", "PM"];

const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 5;

interface ScrollColumnProps {
  items: string[];
  selected: string;
  onSelect: (val: string) => void;
}

const ScrollColumn = ({ items, selected, onSelect }: ScrollColumnProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();

  const selectedIndex = items.indexOf(selected);

  useEffect(() => {
    if (containerRef.current && !isScrolling.current) {
      containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }
  }, [selectedIndex]);

  const handleScroll = useCallback(() => {
    isScrolling.current = true;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      if (!containerRef.current) return;
      const scrollTop = containerRef.current.scrollTop;
      const index = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
      containerRef.current.scrollTo({ top: clampedIndex * ITEM_HEIGHT, behavior: "smooth" });
      onSelect(items[clampedIndex]);
      isScrolling.current = false;
    }, 80);
  }, [items, onSelect]);

  const paddingItems = Math.floor(VISIBLE_ITEMS / 2);

  return (
    <div className="relative" style={{ height: ITEM_HEIGHT * VISIBLE_ITEMS }}>
      {/* Selection highlight */}
      <div
        className="absolute left-0 right-0 border border-border rounded-lg pointer-events-none z-10"
        style={{ top: ITEM_HEIGHT * paddingItems, height: ITEM_HEIGHT }}
      />
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto scrollbar-hide"
        style={{
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Top padding */}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`top-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
        {items.map((item) => (
          <div
            key={item}
            className={`flex items-center justify-center text-lg font-medium cursor-pointer transition-colors ${
              item === selected ? "text-foreground" : "text-muted-foreground/50"
            }`}
            style={{ height: ITEM_HEIGHT, scrollSnapAlign: "center" }}
            onClick={() => {
              onSelect(item);
              if (containerRef.current) {
                const idx = items.indexOf(item);
                containerRef.current.scrollTo({ top: idx * ITEM_HEIGHT, behavior: "smooth" });
              }
            }}
          >
            {item}
          </div>
        ))}
        {/* Bottom padding */}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`bottom-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
      </div>
    </div>
  );
};

interface ScrollTimePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string; // "HH:MM" 24h format
  onSave: (value: string) => void;
}

const parse24hTo12h = (time: string) => {
  const [hStr, mStr] = time.split(":");
  let h = parseInt(hStr) || 0;
  const rawM = parseInt(mStr) || 0;
  const m = Math.floor(rawM / 5) * 5;
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return {
    hour: String(h).padStart(2, "0"),
    minute: String(m).padStart(2, "0"),
    period,
  };
};

const to24h = (hour: string, minute: string, period: string) => {
  let h = parseInt(hour);
  if (period === "AM" && h === 12) h = 0;
  else if (period === "PM" && h !== 12) h += 12;
  return `${String(h).padStart(2, "0")}:${minute}`;
};

const ScrollTimePicker = ({ open, onOpenChange, value, onSave }: ScrollTimePickerProps) => {
  const parsed = parse24hTo12h(value || "12:00");
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);

  useEffect(() => {
    if (open) {
      const p = parse24hTo12h(value || "12:00");
      setHour(p.hour);
      setMinute(p.minute);
      setPeriod(p.period);
    }
  }, [open, value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="text-center">Select time</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center gap-1">
          <div className="w-16">
            <ScrollColumn items={HOURS_12} selected={hour} onSelect={setHour} />
          </div>
          <span className="text-lg font-medium text-muted-foreground">:</span>
          <div className="w-16">
            <ScrollColumn items={MINUTES} selected={minute} onSelect={setMinute} />
          </div>
          <div className="w-16 ml-2">
            <ScrollColumn items={PERIODS} selected={period} onSelect={setPeriod} />
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="ghost"
            className="font-semibold"
            onClick={() => {
              onSave(to24h(hour, minute, period));
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScrollTimePicker;
